"use client";

import { useState } from "react";
import { upgradeToPremium } from "../../api/referralApi";
import { toast } from "react-toastify";
import { TrophyIcon, ClockIcon } from "@heroicons/react/24/outline";

interface ReferralBalanceProps {
  userCoins: number;
  onBalanceUpdate?: () => void;
  onOpenHistory?: () => void;
}

const ReferralBalanceSection = ({ userCoins, onBalanceUpdate, onOpenHistory }: ReferralBalanceProps) => {
  const [isUpgrading, setIsUpgrading] = useState(false);

  const coinsNeeded = Math.max(0, 100 - userCoins);
  const progressPercentage = Math.min(100, (userCoins / 100) * 100);

  const handleUpgrade = async () => {
    if (userCoins < 100) return;

    try {
      setIsUpgrading(true);
      const response = await upgradeToPremium();
      
      if (response.success) {
        toast.success("🎉 Congratulations! You've successfully upgraded to Premium!");
        
        // Refresh balance after upgrade
        if (onBalanceUpdate) {
          onBalanceUpdate();
        }
      } else {
        toast.error(response.message || "Failed to upgrade to premium");
      }
    } catch (error) {
      console.error("Error upgrading to premium:", error);
      toast.error("An error occurred while upgrading. Please try again.");
    } finally {
      setIsUpgrading(false);
    }
  };

  // Get progress bar color
  const getProgressColor = () => {
    if (progressPercentage >= 100) return "bg-gradient-to-r from-green-500 to-emerald-500";
    if (progressPercentage >= 75) return "bg-gradient-to-r from-blue-500 to-cyan-500";
    if (progressPercentage >= 50) return "bg-gradient-to-r from-yellow-500 to-orange-500";
    return "bg-gradient-to-r from-red-500 to-pink-500";
  };

  return (
    <div className="rounded-xl bg-white p-6 shadow-md">
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
            <span className="text-3xl">🪙</span>
          </div>
        </div>
        
        <h3 className="mb-2 text-lg font-medium text-gray-600">
          Your Coin Balance
        </h3>
        
        <p className="mb-6 text-5xl font-bold text-gray-900">
          {userCoins}
        </p>

        {/* Progress Section */}
        <div className="mb-6 rounded-lg bg-[#faeecf] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrophyIcon className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium text-gray-700">
                Premium Upgrade Progress
              </span>
            </div>
            <span className="text-sm font-bold text-gray-900">
              {userCoins}/100
            </span>
          </div>
          
          <div className="mb-3 h-4 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {userCoins >= 100 ? (
            <p className="text-sm font-medium text-green-600">
              🎉 You've earned enough coins for Premium!
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Earn {coinsNeeded} more {coinsNeeded === 1 ? 'coin' : 'coins'} to unlock Premium
            </p>
          )}
        </div>

        {/* Upgrade Button */}
        <button
          onClick={handleUpgrade}
          disabled={userCoins < 100 || isUpgrading}
          className={`mb-3 flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold transition-all ${
            userCoins >= 100
              ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 hover:shadow-lg"
              : "cursor-not-allowed bg-gray-300 text-gray-500"
          }`}
        >
          {isUpgrading ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              Upgrading...
            </>
          ) : userCoins >= 100 ? (
            <>
              <TrophyIcon className="h-5 w-5" />
              Click to Upgrade to Premium
            </>
          ) : (
            <>
              Upgrade to Premium (Need {coinsNeeded} More)
            </>          )}
        </button>

        {/* View Transaction History Button */}
        {onOpenHistory && (
          <button
            onClick={onOpenHistory}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-500 bg-white px-6 py-3 font-semibold text-blue-600 transition-all hover:bg-blue-50"
          >
            <ClockIcon className="h-5 w-5" />
            View Transaction History
          </button>
        )}
      </div>    </div>
  );
};

export default ReferralBalanceSection;